import axios from 'axios';
import { showMessage } from 'app/store/actions/fuse';
import { API_BASE_URL } from '@config';

// * ////////////// STORAGE PROVIDERS ACTIONS ////////////// * //

export const SET_STORAGE_PROVIDERS = 'SET_STORAGE_PROVIDERS';
export const setStorageProviders = providers => ({
	type: SET_STORAGE_PROVIDERS,
	payload: providers
});

export const SET_STORAGE_PROVIDER = 'SET_STORAGE_PROVIDER';
export const setStorageProvider = provider => ({
	type: SET_STORAGE_PROVIDER,
	payload: provider
});

export const SET_STORAGE_PROVIDERS_LOADING = 'SET_STORAGE_PROVIDERS_LOADING';
export const setStorageProvidersLoading = loading => ({
	type: SET_STORAGE_PROVIDERS_LOADING,
	payload: loading
});

export const SET_STORAGE_PROVIDERS_ERROR = 'SET_STORAGE_PROVIDERS_ERROR';
export const setStorageProvidersError = error => ({
	type: SET_STORAGE_PROVIDERS_ERROR,
	payload: error
});

export const CLEAR_STORAGE_PROVIDERS = 'CLEAR_STORAGE_PROVIDERS';
export const clearStorageProviders = () => ({
	type: CLEAR_STORAGE_PROVIDERS
});

export const CLEAR_STORAGE_PROVIDERS_ERROR = 'CLEAR_STORAGE_PROVIDERS_ERROR';
export const clearStorageProvidersError = () => ({
	type: CLEAR_STORAGE_PROVIDERS_ERROR
});

export const SET_SELECTED_PROVIDER = 'SET_SELECTED_PROVIDER';

export const setSelectedProvider = (provider) => ({
    type: SET_SELECTED_PROVIDER,
    payload: provider
});

// * ////////////// HEADERS ////////////// * //
const getHeaders = (organizationId = null) => ({
	headers: {
		admin: true,
		...(organizationId ? { 'x-organization-id': organizationId } : {})
	}
});

// * ////////////// GET ////////////// * //

export const getStorageProviders = (organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.get(`${API_BASE_URL}/api/storage-providers`, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProviders(response.data || []));
			dispatch(setStorageProvidersLoading(false));
			return response.data;
		})
		.catch(err => {
			console.error('Error fetching storage providers:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to fetch storage providers';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

// * ////////////// POST ////////////// * //

export const createStorageProvider = (providerData, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.post(`${API_BASE_URL}/api/storage-providers`, providerData, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setSelectedProvider(response.data));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage provider created successfully'
			}));
			// Refresh the list to ensure all providers are up to date
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error creating storage provider:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to create storage provider';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

// * ////////////// PUT ////////////// * //

export const updateStorageProvider = (providerId, providerData, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.patch(`${API_BASE_URL}/api/storage-providers/${providerId}`, providerData, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvider(response.data));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage provider updated successfully'
			}));
			// Refresh the list
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error updating storage provider:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to update storage provider';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

// * ////////////// DELETE ////////////// * //

export const deleteStorageProvider = (providerId, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.delete(`${API_BASE_URL}/api/storage-providers/${providerId}`, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage provider deleted successfully'
			}));
			// Refresh the list
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error deleting storage provider:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to delete storage provider';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

// * ////////////// POLICY ACTIONS ////////////// * //

export const createStoragePolicy = (providerId, policyData, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	// Add provider ID to policy data
	const dataWithProvider = {
		...policyData,
		provider: providerId
	};

	const request = axios.post(`${API_BASE_URL}/api/storage-policies`, dataWithProvider, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage policy created successfully'
			}));
			// Refresh the list
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error creating storage policy:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to create storage policy';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

export const updateStoragePolicy = (providerId, policyId, policyData, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.put(`${API_BASE_URL}/api/storage-policies/${policyId}`, policyData, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage policy updated successfully'
			}));
			// Refresh the list
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error updating storage policy:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to update storage policy';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

export const deleteStoragePolicy = (providerId, policyId, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.delete(`${API_BASE_URL}/api/storage-policies/${policyId}`, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'Storage policy deleted successfully'
			}));
			// Refresh the list
			dispatch(getStorageProviders(organizationId));
			return response.data;
		})
		.catch(err => {
			console.error('Error deleting storage policy:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to delete storage policy';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};

// * ////////////// PURGE VOLUMEZ RESOURCES ////////////// * //

export const purgeVolumezResources = (providerId, organizationId = null) => dispatch => {
	dispatch(setStorageProvidersLoading(true));

	const request = axios.post(`${API_BASE_URL}/api/providers/${providerId}/purge-volumez`, {}, getHeaders(organizationId));

	return request
		.then(response => {
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: 'VolumeZ resources purged successfully',
				variant: 'success'
			}));
			return response.data;
		})
		.catch(err => {
			console.error('Error purging VolumeZ resources:', err);
			const errorMessage = (err.response && err.response.data && err.response.data.error) ||
							   (err.response && err.response.data && err.response.data.message) ||
							   err.message ||
							   'Failed to purge VolumeZ resources';

			dispatch(setStorageProvidersError(errorMessage));
			dispatch(setStorageProvidersLoading(false));
			dispatch(showMessage({
				message: errorMessage,
				variant: 'error'
			}));
			throw err;
		});
};
